import { useState } from "react";
import axios from "axios";
import { FileSearch, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePick } from "@/components/DateFilters";
import { format } from "date-fns";
import { API } from "@/context/AuthContext";

export const CustomAgeingCompare = ({ testPrefix }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [asOn, setAsOn] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (asOn) fd.append("as_on", asOn);
      const r = await axios.post(`${API}/reports/ap-ageing/custom-compare`, fd, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `custom-ap-ageing-compare${asOn ? `-${asOn}` : ""}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Comparison downloaded");
      setOpen(false);
    } catch (e) {
      let msg = "Comparison failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* blob not JSON */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`${testPrefix}-custom-compare-open`} className="h-9 border-slate-300 text-slate-700">
        <FileSearch className="h-4 w-4 mr-1.5" />Compare SAP custom report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`${testPrefix}-custom-compare-dialog`}>
          <DialogHeader><DialogTitle>Compare with the SAP custom AP ageing</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Upload the Excel downloaded from the SAP custom AP ageing report (Vendor Ageing query). You get an Excel comparing it with the app and the Trial Balance for each document, with the reason for every difference. Takes up to a minute.</p>
          <label className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-3 text-sm cursor-pointer hover:bg-slate-50" data-testid={`${testPrefix}-custom-compare-file-label`}>
            <Upload className="h-4 w-4 text-slate-500" />
            <span className="truncate">{file ? file.name : "Choose .xlsx file"}</span>
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`${testPrefix}-custom-compare-file`} />
          </label>
          <div>
            <p className="text-xs text-slate-600 mb-1">Key date (leave blank to use the one in the file)</p>
            <DatePick label="Use date in file" value={asOn ? new Date(`${asOn}T00:00:00`) : undefined} onChange={(d) => setAsOn(d ? format(d, "yyyy-MM-dd") : "")} testId={`${testPrefix}-custom-compare-date`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testPrefix}-custom-compare-cancel`}>Cancel</Button>
            <Button disabled={!file || busy} onClick={run} className="bg-slate-900 text-white hover:bg-slate-800" data-testid={`${testPrefix}-custom-compare-run`}>
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Compare & download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
